import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import pedidoVentaService, {
  CopiarPedidoRelacionadosResult,
  TenantRelacionadoPedido,
} from '../../../services/pedidoVentaService';
import { getApiErrorMessage } from '../../../utils/apiError';

interface CopiarPedidoRelacionadoDialogProps {
  open: boolean;
  pedidoIds: number[];
  onClose: () => void;
  onCopied: () => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
}

const etiquetaTenant = (tenant: TenantRelacionadoPedido): string => {
  const nombre = tenant.name || tenant.code || `Empresa ${tenant.tenantId}`;
  const codigo = tenant.code && tenant.code !== nombre ? ` (${tenant.code})` : '';
  const rol = tenant.rol ? ` — ${tenant.rol}` : '';
  return `${nombre}${codigo}${rol}`;
};

const CopiarPedidoRelacionadoDialog: React.FC<CopiarPedidoRelacionadoDialogProps> = ({
  open,
  pedidoIds,
  onClose,
  onCopied,
  title = 'Enviar pedidos B',
  description = 'Se envía la Cantidad B a las empresas que elijas. El pedido origen conserva el remanente.',
  confirmLabel = 'Enviar',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantRelacionadoPedido[]>([]);
  const [seleccionados, setSeleccionados] = useState<number[]>([]);
  const [resultado, setResultado] = useState<CopiarPedidoRelacionadosResult | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setResultado(null);
    setSeleccionados([]);
    setTenants([]);
    setLoading(true);
    void pedidoVentaService
      .getTenantsRelacionados()
      .then((lista) => {
        if (!cancelled) setTenants(lista);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err) || 'No se pudieron cargar las empresas relacionadas.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pedidoIds.join(',')]);

  const toggle = (tenantId: number) => {
    setSeleccionados((prev) =>
      prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId],
    );
  };

  const handleCopiar = async () => {
    const ids = pedidoIds.filter((id) => Number.isFinite(id) && id > 0);
    if (seleccionados.length === 0 || ids.length === 0) return;
    try {
      setSaving(true);
      setError(null);
      const acumulado: CopiarPedidoRelacionadosResult = {
        nroFormularioOrigen: ids[0],
        pedidoOrigen: null,
        destinos: [],
      };
      const errores: string[] = [];
      for (const id of ids) {
        try {
          const data = await pedidoVentaService.copiarRelacionados(id, seleccionados);
          acumulado.destinos.push(...data.destinos);
          if (data.pedidoOrigen) acumulado.pedidoOrigen = data.pedidoOrigen;
        } catch (err: unknown) {
          errores.push(`Pedido ${id}: ${getApiErrorMessage(err) || 'No se pudo copiar.'}`);
        }
      }
      if (acumulado.destinos.length === 0 && errores.length > 0) {
        setError(errores.join(' '));
        return;
      }
      setResultado(acumulado);
      const ok = acumulado.destinos.filter((d) => d.ok);
      const fallidos = acumulado.destinos.filter((d) => !d.ok);
      if (ok.length > 0) {
        enqueueSnackbar(
          ok.length === 1
            ? `Pedido copiado a ${ok[0].tenantCode || 'la empresa relacionada'}.`
            : `Pedido copiado a ${ok.length} destinos.`,
          { variant: fallidos.length > 0 || errores.length > 0 ? 'warning' : 'success' },
        );
      } else {
        enqueueSnackbar('No se pudo copiar el pedido a las empresas seleccionadas.', { variant: 'error' });
      }
      if (errores.length > 0) setError(errores.join(' '));
      onCopied();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err) || 'No se pudo copiar el pedido.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : resultado ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              El pedido origen quedó con el remanente y sin cantidad convertida. Para ver un pedido
              destino hay que cambiar a esa empresa.
            </Typography>
            {resultado.destinos.map((destino) => (
              <Alert
                key={`${destino.tenantId}-${destino.nroFormulario ?? 'x'}`}
                severity={destino.ok ? 'success' : 'error'}
              >
                {destino.ok
                  ? `${destino.tenantCode || `Empresa ${destino.tenantId}`}: pedido ${destino.nroFormulario ?? '—'}`
                  : `${destino.tenantCode || `Empresa ${destino.tenantId}`}: ${destino.error || 'No se pudo crear el pedido.'}`}
              </Alert>
            ))}
            {resultado.destinos.length === 0 && (
              <Alert severity="warning">El envío no devolvió destinos.</Alert>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {!error && tenants.length === 0 && (
              <Alert severity="info">No hay empresas relacionadas disponibles.</Alert>
            )}
            <FormGroup>
              {tenants.map((tenant) => (
                <FormControlLabel
                  key={tenant.tenantId}
                  control={
                    <Checkbox
                      checked={seleccionados.includes(tenant.tenantId)}
                      onChange={() => toggle(tenant.tenantId)}
                    />
                  }
                  label={etiquetaTenant(tenant)}
                />
              ))}
            </FormGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
        {!resultado && (
          <Button
            variant="contained"
            onClick={() => void handleCopiar()}
            disabled={saving || loading || seleccionados.length === 0}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Enviando…' : confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CopiarPedidoRelacionadoDialog;
